import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import { fetchTenants } from "../../api/tenantApi";
import { fetchTenantAuditLogs } from "../../api/auditApi";
import PageHeader from "../../components/admin/PageHeader";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatValue = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") {
    const s = JSON.stringify(val);
    return s.length > 40 ? s.slice(0, 37) + "…" : s;
  }
  return String(val);
};

const AuditLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const tenantRes = await fetchTenants();
        const tenantList = Array.isArray(tenantRes)
          ? tenantRes
          : tenantRes?.data?.items || tenantRes?.data || [];

        const enriched = await Promise.all(
          tenantList.map(async (tenant) => {
            let logs = [];
            try {
              const logRes = await fetchTenantAuditLogs(tenant.id);
              logs = Array.isArray(logRes)
                ? logRes
                : logRes?.data?.items || logRes?.data || [];
            } catch {
              // backend audit endpoint not built yet
            }
            const latest = logs.length > 0
              ? logs.reduce((a, b) => (new Date(a.created_at || 0) > new Date(b.created_at || 0) ? a : b))
              : null;
            return {
              ...tenant,
              totalLogs: logs.length,
              latestAction: latest?.action || null,
              latestEntity: latest?.entity_type || null,
              latestActor: latest?.user_id || null,
              latestOld: latest?.old_value_json || null,
              latestNew: latest?.new_value_json || null,
              latestTime: latest?.created_at || null,
            };
          })
        );
        setRows(enriched);
      } catch (err) {
        setError(err.message || "Failed to load audit data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingState label="Loading audit data..." />;
  if (error) return <ErrorState description={error} />;

  const manageLink = (id) => AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", id) + "?tab=audit";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform admin"
        title="Audit logs"
        description="Latest activity per tenant. Click manage to view full audit trail."
      />

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Action</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Entity</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Actor</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Old → New</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Time</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Quick Manage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((tenant) => (
              <tr key={tenant.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-4 font-medium text-slate-900">{tenant.name}</td>
                <td className="px-5 py-4 font-semibold text-slate-950 whitespace-nowrap">
                  {tenant.latestAction || <span className="text-xs text-slate-400 italic">No activity</span>}
                </td>
                <td className="px-5 py-4 text-slate-600">{tenant.latestEntity || "—"}</td>
                <td className="px-5 py-4 text-slate-700">{tenant.latestActor || "—"}</td>
                <td className="px-5 py-4 max-w-[200px]">
                  {tenant.latestAction ? (
                    <span className="block truncate text-xs text-slate-500" title={`${formatValue(tenant.latestOld)} → ${formatValue(tenant.latestNew)}`}>
                      {formatValue(tenant.latestOld)} → {formatValue(tenant.latestNew)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-5 py-4 text-xs text-slate-500 whitespace-nowrap">
                  {formatDateTime(tenant.latestTime)}
                </td>
                <td className="px-5 py-4 text-right">
                  <Link
                    to={manageLink(tenant.id)}
                    className="inline-flex items-center rounded-lg bg-[#096dea] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0959bb]"
                  >
                    Manage →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No tenants found.</div>
        ) : null}
      </div>
    </div>
  );
};

export default AuditLogsPage;
